/**
 * CloverExpirationManager — shows the merchant every item synced from
 * Clover with editable expiration_date + reorder_threshold. These are
 * OUR-side fields (Clover doesn't natively track expiration), stored per
 * clover_item_id and preserved across future syncs.
 *
 * Placed at the top of the Grocery Dashboard's Inventory tab.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Calendar, Package, Save, Search, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const daysUntil = (isoDate) => {
  if (!isoDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${isoDate}T00:00:00`);
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
};

export default function CloverExpirationManager({ businessSlug, isDark, onSaved, refreshKey = 0 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [drafts, setDrafts] = useState({}); // { clover_item_id: {expiration_date, reorder_threshold} }
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/b/${businessSlug}/clover/inventory?limit=1000`);
      setItems(res.data.items || []);
    } catch (e) {
      // silent — this is only meaningful once Clover is connected
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // Re-run when refreshKey changes so parent can trigger a reload after
    // AI-powered inventory imports drop new rows into clover_inventory_snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessSlug, refreshKey]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return items;
    return items.filter(
      (it) =>
        (it.name || "").toLowerCase().includes(query) ||
        (it.barcode || "").toLowerCase().includes(query),
    );
  }, [items, q]);

  const draftFor = (it) => drafts[it.clover_item_id] || {};
  const patchDraft = (id, patch) =>
    setDrafts((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));

  const save = async (it) => {
    const draft = draftFor(it);
    if (Object.keys(draft).length === 0) return;
    setSavingId(it.clover_item_id);
    try {
      await axios.patch(
        `${API}/b/${businessSlug}/clover/inventory/${it.clover_item_id}`,
        draft,
      );
      toast.success(`Updated ${it.name || "item"}`);
      setDrafts((prev) => {
        const { [it.clover_item_id]: _drop, ...rest } = prev;
        return rest;
      });
      await load();
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSavingId(null);
  };

  // Dev/verification: simulate a Clover sale to prove the decrement pipeline works
  // end-to-end without needing a physical Clover device or the Register app.
  const simulateSale = async (it) => {
    if (it.stock_count !== null && it.stock_count !== undefined) {
      toast.info("This item is tracked by Clover itself — simulate against items with '—' stock instead.");
      return;
    }
    const before = it.manual_stock_count;
    if (!before || before <= 0) {
      toast.error("Set a manual stock count first, then click Save.");
      return;
    }
    setSavingId(it.clover_item_id);
    try {
      const r = await axios.post(
        `${API}/b/${businessSlug}/clover/inventory/${it.clover_item_id}/simulate-sale`,
        null,
        { params: { quantity: 1 } },
      );
      toast.success(`Test sale complete — ${it.name} stock: ${before} → ${r.data.new_manual_stock}`);
      await load();
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Simulate failed");
    }
    setSavingId(null);
  };

  // Empty-state — Clover not connected or no items synced yet
  if (!loading && items.length === 0) {
    return (
      <Card className={isDark ? "bg-slate-800 border-slate-700" : ""} data-testid="clover-expiration-empty">
        <CardContent className="p-4 flex items-center gap-3 text-sm">
          <Package className={`w-5 h-5 ${isDark ? "text-slate-400" : "text-slate-500"}`} />
          <div className={isDark ? "text-slate-300" : "text-slate-600"}>
            No Clover inventory synced yet. Connect Clover and click &quot;Sync Now&quot; —
            your items will appear here with editable expiration dates.
          </div>
        </CardContent>
      </Card>
    );
  }

  const nearExpiryCount = items.filter((it) => {
    const d = daysUntil(it.expiration_date);
    return d !== null && d <= 14;
  }).length;

  const effectiveStock = (it) => {
    // Prefer Clover's stock_count (if the merchant has inventory tracking
    // enabled on Clover), fall back to manual_stock_count (tracked here).
    if (it.stock_count !== null && it.stock_count !== undefined) return it.stock_count;
    if (it.manual_stock_count !== null && it.manual_stock_count !== undefined) return it.manual_stock_count;
    return null;
  };

  const lowStockCount = items.filter((it) => {
    const s = effectiveStock(it);
    if (s === null) return false;
    return s <= (it.reorder_threshold ?? 5);
  }).length;

  return (
    <Card className={isDark ? "bg-slate-800 border-slate-700" : ""} data-testid="clover-expiration-manager">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className={`flex items-center gap-2 text-base ${isDark ? "text-white" : "text-slate-800"}`}>
            <Calendar className="w-5 h-5 text-cyan-500" />
            Clover Inventory · Expiration & Reorder
          </CardTitle>
          <div className="flex items-center gap-2 text-xs">
            <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300 hover:bg-emerald-100">
              {items.length} items synced
            </Badge>
            {nearExpiryCount > 0 && (
              <Badge className="bg-red-100 text-red-700 border border-red-300 hover:bg-red-100">
                <AlertTriangle className="w-3 h-3 mr-1" /> {nearExpiryCount} expiring ≤14d
              </Badge>
            )}
            {lowStockCount > 0 && (
              <Badge className="bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-100">
                {lowStockCount} below stock
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="relative mb-3">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? "text-slate-400" : "text-slate-500"}`} />
          <Input
            placeholder="Search by name or barcode…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className={`pl-9 ${isDark ? "bg-slate-900 border-slate-700 text-white" : ""}`}
            data-testid="clover-expiration-search"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={isDark ? "text-slate-400" : "text-slate-500"}>
                <th className="text-left font-medium p-2">Item</th>
                <th className="text-left font-medium p-2">Barcode</th>
                <th className="text-right font-medium p-2">Cost</th>
                <th className="text-right font-medium p-2">Stock</th>
                <th className="text-center font-medium p-2">Reorder at</th>
                <th className="text-center font-medium p-2">Expires</th>
                <th className="text-right font-medium p-2">Save</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 200).map((it) => {
                const draft = draftFor(it);
                const currentExp = draft.expiration_date ?? it.expiration_date ?? "";
                const currentThresh = draft.reorder_threshold ?? it.reorder_threshold ?? 5;
                const cloverTracksStock = it.stock_count !== null && it.stock_count !== undefined;
                const currentManualStock = draft.manual_stock_count ?? it.manual_stock_count ?? "";
                const effStock = cloverTracksStock ? it.stock_count : (draft.manual_stock_count ?? it.manual_stock_count);
                const stockLow =
                  effStock !== null && effStock !== undefined && effStock !== "" &&
                  Number(effStock) <= currentThresh;
                const dLeft = daysUntil(currentExp);
                const dirty = Object.keys(draft).length > 0;
                return (
                  <tr
                    key={it.clover_item_id}
                    className={isDark ? "border-t border-slate-700" : "border-t border-slate-200"}
                    data-testid={`clover-item-row-${it.clover_item_id}`}
                  >
                    <td className={`p-2 ${isDark ? "text-white" : "text-slate-800"}`}>
                      {it.name || <em className="opacity-60">Unnamed</em>}
                    </td>
                    <td className={`p-2 font-mono text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                      {it.barcode || "—"}
                    </td>
                    <td className="p-2 text-right">
                      {(() => {
                        // Effective cost: manual override wins; else Clover's cost_cents; else empty.
                        const currentManualCost = draft.manual_cost_cents ?? it.manual_cost_cents;
                        const cloverHasCost = (it.cost_cents ?? 0) > 0;
                        // Merchant-set cost always editable. Clover-provided cost shown read-only
                        // with an "override" affordance.
                        const displayValue = currentManualCost !== null && currentManualCost !== undefined
                          ? (currentManualCost / 100).toFixed(2)
                          : cloverHasCost
                            ? (it.cost_cents / 100).toFixed(2)
                            : "";
                        return (
                          <div className="flex items-center justify-end gap-1">
                            <span className={`text-[10px] ${isDark ? "text-slate-500" : "text-slate-400"}`}>$</span>
                            <Input
                              type="number"
                              step="0.01"
                              min={0}
                              value={displayValue}
                              placeholder={cloverHasCost ? "(Clover)" : "—"}
                              onChange={(e) =>
                                patchDraft(it.clover_item_id, {
                                  manual_cost_cents: e.target.value === ""
                                    ? null
                                    : Math.round(Number(e.target.value) * 100),
                                })
                              }
                              className={`h-8 w-20 text-right text-xs ${isDark ? "bg-slate-900 border-slate-700 text-white" : ""} ${
                                cloverHasCost && (currentManualCost === null || currentManualCost === undefined) ? "opacity-60" : ""
                              }`}
                              data-testid={`clover-item-cost-${it.clover_item_id}`}
                              title={cloverHasCost
                                ? "Clover has a cost; edit to override for profit calc"
                                : "Set your buy cost — used for profit tracking"}
                            />
                          </div>
                        );
                      })()}
                    </td>
                    <td className={`p-2 text-right ${stockLow ? "text-amber-500 font-semibold" : isDark ? "text-slate-300" : "text-slate-700"}`}>
                      {cloverTracksStock ? (
                        // Clover manages this — read-only, with a subtle badge
                        <div className="flex items-center justify-end gap-1.5">
                          <span>{it.stock_count}</span>
                          <span className={`text-[9px] px-1 py-0.5 rounded ${isDark ? "bg-slate-700 text-slate-400" : "bg-slate-100 text-slate-500"}`}
                                title="Stock is tracked by Clover for this item">
                            Clover
                          </span>
                        </div>
                      ) : (
                        // Merchant-tracked — editable input
                        <Input
                          type="number"
                          min={0}
                          value={currentManualStock}
                          placeholder="—"
                          onChange={(e) =>
                            patchDraft(it.clover_item_id, {
                              manual_stock_count: e.target.value === "" ? null : Number(e.target.value),
                            })
                          }
                          className={`h-8 w-20 text-right text-xs ml-auto ${isDark ? "bg-slate-900 border-slate-700 text-white" : ""}`}
                          data-testid={`clover-item-stock-${it.clover_item_id}`}
                          title="Manual stock — auto-decrements after each sale"
                        />
                      )}
                    </td>
                    <td className="p-2 text-center">
                      <Input
                        type="number"
                        min={0}
                        value={currentThresh}
                        onChange={(e) =>
                          patchDraft(it.clover_item_id, {
                            reorder_threshold: Number(e.target.value),
                          })
                        }
                        className={`h-8 w-16 text-center text-xs mx-auto ${isDark ? "bg-slate-900 border-slate-700 text-white" : ""}`}
                        data-testid={`clover-item-reorder-${it.clover_item_id}`}
                      />
                    </td>
                    <td className="p-2 text-center">
                      <Input
                        type="date"
                        value={currentExp || ""}
                        onChange={(e) =>
                          patchDraft(it.clover_item_id, {
                            expiration_date: e.target.value || "",
                          })
                        }
                        className={`h-8 w-36 text-xs mx-auto ${isDark ? "bg-slate-900 border-slate-700 text-white" : ""} ${
                          dLeft !== null && dLeft <= 7
                            ? "border-red-400"
                            : dLeft !== null && dLeft <= 14
                            ? "border-amber-400"
                            : ""
                        }`}
                        data-testid={`clover-item-expiry-${it.clover_item_id}`}
                      />
                      {dLeft !== null && (
                        <div className={`text-[10px] mt-0.5 ${
                          dLeft < 0
                            ? "text-red-500 font-semibold"
                            : dLeft <= 7
                            ? "text-red-500"
                            : dLeft <= 14
                            ? "text-amber-500"
                            : isDark ? "text-slate-400" : "text-slate-500"
                        }`}>
                          {dLeft < 0 ? `Expired ${-dLeft}d ago` : `${dLeft}d left`}
                        </div>
                      )}
                    </td>
                    <td className="p-2 text-right">
                      <div className="inline-flex gap-1">
                        <Button
                          size="sm"
                          variant={dirty ? "default" : "outline"}
                          disabled={!dirty || savingId === it.clover_item_id}
                          onClick={() => save(it)}
                          className="h-8"
                          data-testid={`clover-item-save-${it.clover_item_id}`}
                          title="Save changes"
                        >
                          <Save className="w-3.5 h-3.5" />
                        </Button>
                        {!cloverTracksStock && (it.manual_stock_count ?? 0) > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={savingId === it.clover_item_id}
                            onClick={() => simulateSale(it)}
                            className={`h-8 ${isDark ? "border-amber-500/40 text-amber-400 hover:bg-amber-500/10" : "border-amber-500 text-amber-600 hover:bg-amber-50"}`}
                            data-testid={`clover-item-simulate-${it.clover_item_id}`}
                            title="Simulate a sale (test decrement without ringing on Clover)"
                          >
                            <Zap className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length > 200 && (
            <p className={`text-xs mt-2 ${isDark ? "text-slate-500" : "text-slate-400"}`}>
              Showing first 200 items. Refine your search to narrow the list.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
